/**
 * Generic Sorting Utilities
 * 
 * Provides reusable sorting functions for table data across the application.
 * Extracted from component-specific sorting logic to improve maintainability.
 */

// Re-export SortOrderItem type for convenience (also defined in VirtualizedSpendTable)
export interface SortOrderItem {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Generic multi-column sorting function
 * 
 * @param data Array of items to sort
 * @param sortOrder Array of sort criteria (applied in order)
 * @param options Optional configuration:
 *   - getValue: Custom function to extract value from item by field name
 *   - defaultSort: Default sort function when sortOrder is empty
 *   - specialHandlers: Map of field names to custom comparison functions
 * 
 * @returns Sorted array (new array, original unchanged)
 */
export function sortByOrder<T>(
  data: T[],
  sortOrder: SortOrderItem[],
  options?: {
    getValue?: (item: T, field: string) => unknown;
    defaultSort?: (a: T, b: T) => number;
    specialHandlers?: Record<string, (a: T, b: T, direction: 'asc' | 'desc') => number>;
  }
): T[] {
  if (!Array.isArray(data)) {
    return [];
  }

  // If no sort order, use default sort or return as-is
  if (!sortOrder.length) {
    if (options?.defaultSort) {
      return [...data].sort(options.defaultSort);
    }
    return [...data];
  }

  const getValue = options?.getValue || ((item: T, field: string) => {
    const itemObj = item as Record<string, unknown>;
    return itemObj[field];
  });

  return [...data].sort((a, b) => {
    for (const { field, direction } of sortOrder) {
      // Check for special handler first
      if (options?.specialHandlers?.[field]) {
        const result = options.specialHandlers[field](a, b, direction);
        if (result !== 0) return result;
        continue;
      }

      // Default comparison logic
      let aValue = getValue(a, field);
      let bValue = getValue(b, field);

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (aValue !== bValue) {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        continue;
      }

      // Handle string comparison
      const aStr = String(aValue ?? '').toLowerCase();
      const bStr = String(bValue ?? '').toLowerCase();
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
    }
    return 0;
  });
}

/**
 * Sort campaign transactions with special handling for spend field
 * Puts $0 spend values at the end regardless of sort direction
 */
export function sortCampaignTransactions(
  data: Array<Record<string, unknown>>,
  sortOrder: SortOrderItem[]
): Array<Record<string, unknown>> {
  return sortByOrder(data, sortOrder, {
    specialHandlers: {
      spend: (a, b, direction) => {
        const getNumericValue = (item: Record<string, unknown>) => {
          const value = item.spend;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(String(value || '').replace(/[^\d.]/g, '')) || 0;
          return parsed;
        };

        const aNum = getNumericValue(a);
        const bNum = getNumericValue(b);

        // $0 always goes last
        if (aNum === 0 && bNum !== 0) return 1;
        if (aNum !== 0 && bNum === 0) return -1;

        // Normal numeric sort
        if (aNum !== bNum) {
          return direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        return 0;
      }
    }
  });
}

/**
 * Sort month rows with default newest-first when no sort order
 */
export function sortMonthRows<T extends { monthKey: string }>(
  data: T[],
  sortOrder: SortOrderItem[]
): T[] {
  return sortByOrder(data, sortOrder, {
    defaultSort: (a, b) => (a.monthKey < b.monthKey ? 1 : -1), // newest first
    getValue: (item, field) => {
      // Special handling: monthLabel sorts by monthKey
      if (field === 'monthLabel') {
        return item.monthKey;
      }
      const itemObj = item as Record<string, unknown>;
      return itemObj[field];
    }
  });
}

